import React, { useState } from "react";
import { useEditBookedEventMutation } from "../../api/api";
import SortupDialog from "../../components/common/SortupDialog";
import { useToast } from "../../@/components/ui/use-toast";

interface RescheduleBookingProps {
  id: number;
  startTime: string;
  endTime: string;
  calenderEventId: string;
  timeZone: string;
  onClose: () => void;
  onSave: () => void;
}

const RescheduleBooking = (props: RescheduleBookingProps) => {
  const [editBooking, { isLoading }] = useEditBookedEventMutation();
  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [startTime, setStartTime] = useState(props.startTime);
  const [endTime, setEndTime] = useState(props.endTime);
  const [isInvalidTime, setIsInvalidTime] = useState(false);
  const toast = useToast();

  React.useEffect(() => {
    setStartTime(props.startTime);
    setEndTime(props.endTime);
  }, [props.startTime, props.endTime]);

  const handleTimeChange = (newStartTime: string, newEndTime: string) => {
    // Reset invalid state by default
    setIsInvalidTime(false);

    // Always validate whenever either time is changed
    const start = new Date(newStartTime);
    const end = new Date(newEndTime);

    if (end < start) {
      setIsInvalidTime(true);
      toast.toast({
        title: "Error!",
        description: "End time cannot be before start time.",
      });
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    props.onClose();
  };

  const handleSaveCloseDialog = () => {
    setIsDialogOpen(false);
    props.onSave();
  };

  const handleSave = async () => {
    if (isInvalidTime) return;

    try {
      await editBooking({
        editBookedEventDto: {
          id: props.id,
          startTime: new Date(startTime).toISOString(),
          endTime: new Date(endTime).toISOString(),
          calenderEventId: props.calenderEventId,
          timeZone: props.timeZone,
        },
      }).unwrap();
      handleSaveCloseDialog();
    } catch (error: any) {
      toast.toast({
        title: "Error",
        description: error.data?.message || "Failed to update meeting",
      });
      console.error("Error updating booking:", error);
    }
  };

  // Function to format time for input
  const formatTime = (time: string) => {
    const date = new Date(time);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  return (
    <SortupDialog
      isOpen={isDialogOpen}
      onClose={handleCloseDialog}
      header="Reschedule Booking Details"
      bodyClassName="w-[28vw]"
      footerButtons={[
        {
          name: "Save",
          onClick: handleSave,
          className:
            isLoading || isInvalidTime
              ? "opacity-50 cursor-not-allowed text-white"
              : "text-white",
          disabled: isLoading || isInvalidTime,
        },
        {
          name: "Cancel",
          className: "bg-white text-gray-900 border",
          disabled: false,
          onClick: handleCloseDialog,
        },
      ]}
    >
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Appointment Date
        </label>
        <input
          type="date"
          value={startTime.split("T")[0]}
          onChange={(e) => {
            const newDate = e.target.value;
            const newStartTime = newDate + "T" + startTime.split("T")[1];
            const newEndTime = newDate + "T" + endTime.split("T")[1];
            setStartTime(newStartTime);
            setEndTime(newEndTime);
            handleTimeChange(newStartTime, newEndTime);
          }}
          onFocus={(e) => e.target.showPicker && e.target.showPicker()}
          className={`focus:outline-customGreenColor mt-1 p-2 border ${
            isInvalidTime ? "border-red-500" : "border-gray-300"
          } rounded-md w-full`}
        />
        <label className="block mt-4 text-sm font-medium text-gray-700">
          Appointment Start Time
        </label>
        <input
          type="time"
          value={formatTime(startTime)}
          onChange={(e) => {
            const newTime = e.target.value;
            const newStartTime = startTime.split("T")[0] + "T" + newTime;
            setStartTime(newStartTime);
            handleTimeChange(newStartTime, endTime);
          }}
          onFocus={(e) => e.target.showPicker && e.target.showPicker()}
          className={`focus:outline-customGreenColor mt-1 p-2 border ${
            isInvalidTime ? "border-red-500" : "border-gray-300"
          } rounded-md w-full`}
        />
        <label className="block mt-4 text-sm font-medium text-gray-700">
          Appointment End Time
        </label>
        <input
          type="time"
          value={formatTime(endTime)}
          onChange={(e) => {
            const newTime = e.target.value;
            const newEndTime = endTime.split("T")[0] + "T" + newTime;
            setEndTime(newEndTime);
            handleTimeChange(startTime, newEndTime);
          }}
          onFocus={(e) => e.target.showPicker && e.target.showPicker()}
          className={`focus:outline-customGreenColor mt-1 p-2 border ${
            isInvalidTime ? "border-red-500" : "border-gray-300"
          } rounded-md w-full`}
        />
      </div>
    </SortupDialog>
  );
};

export default RescheduleBooking;
